<!-- Today: the day's proposals as a running timeline.

     It used to be four tickets from one pad, then eight sections one per
     strategy, stepped through with "Set 2 of 3" and a pair of arrows. All of
     that answered the wrong question. A punter opening this screen wants to
     know what is about to start and what they have already seen — not which
     snapshot of the morning they happen to be looking at.

     So the day is ONE LIST, ordered by the earliest kick-off on each ticket,
     with a Now rule where the next one starts. Scroll up for what has been and
     gone (dimmed, legs reading "started", every stamp kept); scroll down for
     what is still to come. The screen opens pinned to Now. The strategy is an
     eyebrow on the ticket rather than the outer grouping — see KickoffGroup.

     NOTHING ON THIS SCREEN FETCHES ANYTHING except the two controls that
     always did: pull-to-refresh and Refresh, which are one call. Deal more re-runs the
     strategies over the board already on the device, and so does the refill
     that happens when a ticket expires. Both are free.

     Each ticket carries the one action that goes with it. Place bet writes the
     record straight into History as Pending, from here, because walking to a
     detail screen to say "yes I placed it" was a door in front of the only
     thing anyone came to do. The whole of each ticket is still the button that
     opens it.

     Before the day's odds are in, the pad holds one blank ticket saying so.
     The terms are printed at the foot, the way a ticket's are.

     THE TOP OF THE PAGE IS ONE LINE. It was six: a large title saying the word
     the toolbar already said, a dateline, a prices bar, a row holding the set
     stepper and "Another set", and a sentence about yesterday — a whole phone
     screen before the first ticket, on the one page nobody opens for anything
     but the tickets. The two controls went to the toolbar, where a page action
     belongs; the prices and the day are one line of small print; and the
     sentence about yesterday is gone entirely: the feed prints yesterday's
     tickets themselves, above the rule. -->
<template>
  <IonPage>
    <AppHeader title="Today">
      <IonButtons slot="end">
        <!-- ONE TRAILING ACTION (DESIGN.md, Chrome). Refresh is the pull; the
             builder is Today's own sheet ("Change" on the ask card, and "Build
             your own" at the foot of the old pad); Share sits on the list it
             shares; How it works is a link at the foot of the list. -->
        <IonButton aria-label="Settings" @click="goSettings">
          <IonIcon slot="icon-only" :icon="settingsOutline" aria-hidden="true" />
          <span class="sr-only">Settings</span>
        </IonButton>
      </IonButtons>
    </AppHeader>

    <!-- `scroll-events` is off by default and the segment needs them: its value
         is read off the scroll position, throttled, so that the control says
         where the reader is rather than where they last tapped. -->
    <IonContent ref="content" :fullscreen="true" :scroll-events="true" @ionScroll="onScroll">
      <!-- Pulling down is a fetch, and a fetch is the thing a break is a break
           from. The gesture goes with the rest of them. -->
      <IonRefresher v-if="!onBreak" slot="fixed" @ionRefresh="refresh">
        <IonRefresherContent />
      </IonRefresher>

      <div class="pad-page" :class="{ 'pad-page--sample': sampleFeed, 'pad-page--ask': flags.ASK }">
        <!-- The whole top of the page: the day, where the prices came from,
             what is left to spend, and what the odds assume. One line. The
             iOS large title went with the rest of it — the toolbar already
             says "Today", and a second copy of the word was 80px of the first
             screen spent saying it twice.

             Not on the sample board. Every figure on that line — when the
             prices were read, what is left on the key, what a refetch would
             cost — is about a key this device does not have, and the banner
             below takes its place. -->
        <PricesBar v-if="!onBreak && !sampleMode" ref="pricesBar" />

        <!-- A BOARD BEFORE A KEY.
             The app used to open on a form: sign in, then paste a key, then
             come back tomorrow when the prices drop. Nobody decides whether a
             betting app is worth a signup by reading its setup instructions,
             so the feed underneath is a real pad run over the sample day —
             every strategy, every tier, the timeline and the fold — and this
             says out loud that it is not today's.

             THE CARD AND THE BANNER ARE TWO DIFFERENT JOBS. The card is read
             once, at the top, and carries what it costs and the way in; the
             banner is one line that rides the scroll, because a punter three
             screens down a board of Collingwood and Penrith has to be able to
             find out it is a sample without scrolling back up. -->
        <template v-if="sampleFeed">
          <div class="onboarding onboarding--sample">
            <h2 class="onboarding-title">{{ sampleCard.title }}</h2>
            <p class="onboarding-text">{{ sampleCard.text }}</p>
            <!-- The credit maths, in the one sentence the Providers section and
                 the board picker also print. It is worked out from the boards
                 actually ticked and from the provider's own cost, so nobody is
                 quoted a figure that is not theirs — see lib/credits.js. -->
            <p class="onboarding-text sample-credits">{{ creditSentence }}</p>
            <IonButton expand="block" @click="sampleCard.go">{{ sampleCard.label }}</IonButton>
            <p class="onboarding-note">{{ sampleCard.note }}</p>
          </div>

          <p class="sample-banner">Sample board · yesterday's shape, today's date. Add your free key to see today.</p>
        </template>

        <!-- The sentence about yesterday's pad used to print here, with a "See
             them ›" into History. The feed IS yesterday's pad now — the day's
             own tickets, with the stamps they earned, directly above the Now
             rule — so a summary of it above the record would be the same
             morning said twice. -->

        <!-- A BREAK OUTRANKS EVERY OTHER STATE ON THIS SCREEN. Not a banner
             over the feed and not a row of disabled buttons: one sheet where
             the day's tickets were, because a feed you can read but not act on
             is the same feed. Nothing here fetches, and nothing here is a way
             back — the break ends when the day it names arrives. -->
        <template v-if="onBreak">
          <section class="sheet break-sheet">
            <!-- Not `.section-title`: that is the app's eyebrow — uppercase and
                 tracked — and this is a sentence somebody wrote to themselves,
                 not a label on a list. It is the hero of the screen because it
                 is the only thing on it. -->
            <h2 class="break-title">{{ breakLine(riskProfile) }}</h2>
            <p class="break-text">
              No tickets, no prices and nothing to place until then. Today will be here when you get back.
            </p>
            <!-- The help line is the page's own footer, printed on every Today
                 and directly under this sheet on a short screen. Saying the same
                 phone number twice a hundred pixels apart reads as boilerplate,
                 which is the one thing that line must never read as. -->
          </section>
        </template>

        <!-- The two cards in full, for the case the sample board is NOT there
             to soften them: QA's `empty` fixture, and a device whose sample
             failed to build. `sampleFeed` is what decides, not the gate — the
             gate is still the thing that says which of the two doors this
             person is standing at. -->
        <template v-else-if="gate === 'signed-out' && !sampleFeed">
          <div class="onboarding">
            <h2 class="onboarding-title">Sign in to see today's board</h2>
            <p class="onboarding-text">The day's prices are fetched once and shared with everyone who has access. Legwork asks who you are before it hands them over.</p>
            <IonButton expand="block" @click="goSignIn">Sign in</IonButton>
            <p class="onboarding-note">No access yet? The sign-in page has a waitlist.</p>
          </div>
        </template>

        <template v-else-if="gate === 'unconfigured' && !sampleFeed">
          <div class="onboarding">
            <h2 class="onboarding-title">Set up your odds feed</h2>
            <p class="onboarding-text">Legwork needs an API key from The Odds API to fetch today's prices. The free plan gives you 500 credits a month, plenty for daily use.</p>
            <p class="onboarding-text sample-credits">{{ creditSentence }}</p>
            <ol class="onboarding-steps">
              <li>Sign up at <a href="https://the-odds-api.com" target="_blank" rel="noopener">the-odds-api.com</a></li>
              <li>Copy your API key from your dashboard</li>
              <li>Paste it in Settings</li>
            </ol>
            <IonButton expand="block" @click="goSettings">Open Settings</IonButton>
            <p class="onboarding-note">Your key stays on this device. It's sent securely over HTTPS to fetch odds, never stored on our server.</p>
          </div>
        </template>

        <!-- ASK, THEN ANSWER (docs/tips-ux.md). The pad below deals itself the
             moment a board lands and nobody can tell it what they want; this
             asks first and answers from the same cached board, on request.
             Behind the ASK flag, so the old pad is one switch away until the
             new one has been verified on a device. -->
        <template v-else-if="flags.ASK && (hasMarkets || hasRacingCard || beforeDropAsk)">
          <AskHome
            :can-share="!sampleMode"
            :sharing="shareSome.busy.value"
            @place="askToPlace"
            @copy="copyLegs"
            @share-tickets="shareChosen"
            @how="showHowItWorks = true"
          />
        </template>

        <template v-else-if="hasMarkets">
          <!-- NOT A FILTER. Past · Upcoming · All hid two thirds of the day to
               show one, which is the opposite of what a feed is for: the owner
               wanted to scroll from yesterday's results through to tomorrow's
               board without choosing a view first. So the three positions are
               JUMPS — Earlier scrolls to the top of what is loaded (and pulls
               one more day in behind it), Now to the rule, Later to the first
               cluster past it — and the value follows the SCROLL, which makes
               the control a position indicator rather than a state.

               AND IT STICKS. It sat in the flow above the tickets, which was
               fine for a filter — you tapped it once and read what it gave you
               — and useless for a jump: the screen opens pinned to Now, two
               thousand pixels down, and the control was off the top of the
               page before anyone could reach it. Sticky is the same place in
               the layout, kept. -->
          <!-- AND THE ONE FILTER ON THE SCREEN RIDES WITH THEM. Racing landed
               on this feed and Today stopped being one kind of paper: a punter
               who does not follow the horses was scrolling past half the roll
               to find the football. Two toggles, both on by default, so the
               feed is still the whole day unless somebody says otherwise —
               and they sit in the bar that is already stuck to the top, where
               the answer to "where has everything gone" is one tap away.

               A ROW OF SHAPES, NOT A SECOND SEGMENT. Ionic's segment is a
               one-of-N control and these are two independent switches; and a
               second 44px bar under the first would spend a fifth of a 320px
               screen on something used twice a month. They are quiet when ON
               — carbon on paper, which is the state the screen is in almost
               always — and struck through when off, which is the pad's own
               mark for a thing that is not running. -->
          <div class="timeline-view jump-bar">
            <IonSegment :value="region" class="jumps" @ionChange="onJump">
              <IonSegmentButton value="earlier"><IonLabel>Earlier</IonLabel></IonSegmentButton>
              <IonSegmentButton value="now"><IonLabel>Now</IonLabel></IonSegmentButton>
              <IonSegmentButton value="later"><IonLabel>Later</IonLabel></IonSegmentButton>
            </IonSegment>

            <KindFilter :filter="feedFilter" @toggle="toggleKind" />
          </div>

          <!-- The day's answer, once, above the list. It is about today's
               ranking as a whole, and the list is no longer ordered by it. -->
          <p v-if="showVerdict" class="verdict">{{ VERDICT_LINE }}</p>

          <!-- ONE MORE DAY AT THE TOP, and only when the reader actually gets
               there. The trail runs 60 days and drawing all of it would be a
               morning's paper nobody asked for; this loads the next day back
               as the reader reaches the top of what is already drawn, and the
               scroll is nudged down by exactly what was inserted so the page
               does not jump under their thumb. Reading is not fetching: every
               day here came off the device. -->
          <IonInfiniteScroll
            v-if="!trailDone"
            position="top"
            threshold="200px"
            @ionInfinite="loadEarlier"
          >
            <IonInfiniteScrollContent loading-spinner="dots" loading-text="Earlier" />
          </IonInfiniteScroll>

          <template v-for="section in sections" :key="section.key">
            <!-- THE FOLD. A ruled heading on the desk between the sheets, not
                 on one of them: it is the boundary between two kinds of
                 ticket, and the screen opens scrolled to it. -->
            <h2
              v-if="section.kind === 'now'"
              class="section-title section-title--ruled now-rule"
              data-feed-key="now"
            >Now</h2>

            <!-- WHAT TO BET, directly under the rule the screen opens at and
                 above the first kick-off cluster. The feed is a clock and
                 answers "what starts next"; this answers the question somebody
                 actually opened the app with.

                 IT SITS HERE RATHER THAN AT THE TOP OF THE ROLL, and that is
                 the whole of why it is inside the loop: the top of the roll is
                 yesterday's record, two thousand pixels above where the screen
                 opens, and a sheet up there is a sheet nobody sees. Under the
                 rule it is the first thing read, every morning, with the sticky
                 jump bar above it.

                 Never over the sample board — a recommendation off somebody
                 else's Saturday is not a recommendation — and never on a break,
                 where the break sheet owns the whole page. See
                 `lib/best-bets.js` for what earns a place on it. -->
            <BestBets
              v-if="section.kind === 'now' && showBestBets"
              :model="bestBetsModel"
              :record="closeRecord"
              @explain="showHowItWorks = true"
            />

            <p
              v-else-if="section.kind === 'note'"
              class="small-print feed-note"
              :data-feed-key="section.key"
            >{{ section.text }}</p>

            <ProposedDay
              v-else-if="section.kind === 'day'"
              :data-feed-key="section.key"
              :label="section.label"
              :proposals="section.proposals"
              :now="now"
            />

            <KickoffGroup
              v-else
              :data-feed-key="section.key"
              :label="section.label"
              :entries="section.entries"
              :universe="universe"
              :now="now"
              :past="section.past"
              :proof="proofByStrategy"
              density="feed"
              @open="open"
              @place="askToPlace"
              @copy="copyLegs"
              @history="goPending"
              @explain="explain"
            />
          </template>

          <!-- NOTHING ON THE FEED, and the two reasons are different answers.
               A day that has printed nothing says so. A day whose tickets are
               all behind a toggle says WHICH toggle, and offers the way back:
               the control is stuck to the top of the screen and a reader who
               has scrolled to the foot should not have to go looking for it to
               find out what happened to their morning. -->
          <p v-if="!listed" class="small-print timeline-empty">{{ emptyLine }}</p>
          <p v-if="!listed && filteredOut" class="deal-more show-all">
            <button type="button" class="link" @click="showAll">Show everything ›</button>
          </p>

          <!-- DEAL MORE, AT THE FOOT OF THE LIST rather than in the toolbar. It
               adds to the list and never fetches, so it belongs where the list
               runs out; and the toolbar slot it held was the one Refresh
               needed — the shuffle glyph read as "refresh" to anyone who
               did not know the difference. -->
          <p v-if="canDeal && listed" class="deal-more">
            <button type="button" class="link" @click="dealMoreTickets">Deal more ›</button>
            <!-- What it does, and — because a button that adds to a list is a
                 button somebody is entitled to fear — what it does NOT do:
                 nothing already on the feed moves, and a placed ticket is a
                 record that cannot be dealt away.
                 "Different" is the promise the deal now actually keeps: it
                 will not print the same legs back, nor one of today's tickets
                 with a leg off or on, and it no longer stops because a tier
                 already has two. -->
            <span class="small-print deal-more-note">Different multis from the same prices · nothing is fetched.</span>
          </p>

          <!-- The same builder the header's + opens, said in words at the foot
               of the list — where somebody who has read every ticket and wants
               a different one is looking. -->
          <p v-if="canBuild && listed" class="deal-more">
            <button type="button" class="link" @click="openBuilder">Build your own ›</button>
            <span class="small-print deal-more-note">Your legs, your book, priced off this board. Nothing is fetched.</span>
          </p>

          <!-- WHAT WAS HIDDEN, AND THE WAY BACK. Hiding a ticket is the one
               thing on this screen that takes something off it, so the way to
               undo it cannot live only in a toast that lasts seven seconds —
               the count is at the foot for the rest of the day, and the toast
               says so. Collapsed by default: this is a control for the rare
               morning somebody changes their mind, not a second feed. -->
          <div v-if="hiddenToday.length" class="hidden-foot">
            <button
              type="button"
              class="link hidden-toggle"
              :aria-expanded="showHidden ? 'true' : 'false'"
              @click="showHidden = !showHidden"
            >{{ hiddenFootLabel(hiddenToday.length) }}</button>
            <ul v-if="showHidden" class="hidden-list">
              <li v-for="row in hiddenToday" :key="row.id" class="hidden-row">
                <span class="small-print hidden-name">{{ hiddenRowLine(row) }}</span>
                <!-- One at a time. "Show them all" would be a second control
                     for a list that is rarely longer than two. -->
                <button type="button" class="link hidden-show" @click="restore(row.id)">Show</button>
              </li>
            </ul>
          </div>

          <!-- The strategies that printed nothing today, gathered at the foot
               rather than scattered through the clock — "no leg cleared the
               edge test" is an ANSWER and belongs on the screen, but it is not
               a thing that starts at 7:20 pm and it must not interrupt a list
               that is read as a timeline. -->
          <section v-if="showQuiet || refusedLine" class="sheet quiet-sheet">
            <h2 class="section-title quiet-title">Nothing today</h2>
            <p v-for="q in quiet" :key="q.id" class="small-print quiet-note">
              <strong>{{ q.name }}</strong> · {{ q.note }}
            </p>
            <!-- AND THE REFUSALS, at the foot of the refusals. A strategy that
                 printed nothing says why in the lines above; this says how many
                 individual legs the gate turned down underneath them, and where
                 to read what they were worth. It is the same answer at a finer
                 grain, so it belongs on the same sheet.

                 A LINK, NOT A LIST. Fifty rows of "Collingwood at 2.10, below
                 the EV floor" is a log file, and the only question worth asking
                 of it — were those legs beating the close — is arithmetic that
                 Insights already does. -->
            <p v-if="refusedLine" class="small-print quiet-note quiet-refused">
              <button type="button" class="link" @click="goRefused">{{ refusedLine }}</button>
            </p>
          </section>
        </template>

        <Ticket v-else :multi="null" :blank-title="blank.title" :blank-note="blank.note" />

        <!-- The picker, opened on nothing: every side on today's board, ranked
             by what its price is worth against the other books. What comes out
             is a proposal like any other — it lands on this feed under its own
             kick-off and the paper trail scores it beside the engine's. -->
        <AddLegSheet
          :is-open="building"
          mode="build"
          :candidates="buildRows"
          :on-ticket="[]"
          :under-way="startedOnBoard"
          :book-key="riskProfile.bookmaker"
          :has-board="hasMarkets"
          :preview="previewBuild"
          :save="printBuilt"
          :now="now"
          @close="building = false"
        />

        <footer class="terms">
          <p>Legwork proposes tickets. It never takes a bet or holds money. 18+ only.</p>
          <p>
            Gambling Help <a href="tel:1800858858">1800 858 858</a>
            · <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener">gamblinghelponline.org.au</a>
            · Self-exclude at <a href="https://www.betstop.gov.au" target="_blank" rel="noopener">BetStop</a>
          </p>
        </footer>
      </div>
    </IonContent>

    <IonModal :is-open="showHowItWorks" @didDismiss="showHowItWorks = false">
      <IonHeader>
        <IonToolbar>
          <IonTitle>How it works</IonTitle>
          <IonButtons slot="end">
            <IonButton @click="showHowItWorks = false">Done</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div class="pad-page how-it-works">
          <section>
            <h2>What Legwork does</h2>
            <p>Every day, Legwork looks at head-to-head sports odds across Australia and builds four multi-bet tickets, one for each risk level. That is one <TermLink id="strategy">strategy</TermLink> of several: you can swap it, tune it, or test it against past seasons before you trust it.</p>
          </section>

          <!-- Every leg on every tier is a favourite. The only thing that
               changes between the four is how many of them are stacked, so
               these four lines say a leg count and nothing else. -->
          <section>
            <h2>The four ways</h2>
            <p>Every leg is a <TermLink id="favourite">favourite</TermLink>, about $1.67 or shorter: a 57% chance once the bookmaker's <TermLink id="vig">margin</TermLink> is taken out. <strong>The four ways are leg counts</strong>: two, three, four or five, six to eight. Every leg costs a little margin, and the expected return is printed on each ticket.</p>
            <p><strong>The colour is the chance, not the leg count.</strong> Each ticket is coloured by what its own legs actually multiply out to, so on a thin board the two-leg ticket and the three-leg one can both print Medium risk, and a ticket saying "Low risk" says it because the chance beside it earned the word.</p>
            <div class="tier-explain">
              <div class="tier-item">
                <span class="tier-dot" style="--tier-ink: var(--tier-low)"></span>
                <div>
                  <strong>Low risk</strong>
                  <p>Better than a coin flip: 45% or likelier.</p>
                </div>
              </div>
              <div class="tier-item">
                <span class="tier-dot" style="--tier-ink: var(--tier-medium)"></span>
                <div>
                  <strong>Medium risk</strong>
                  <p>Between 18% and 45%, roughly one in five to one in two.</p>
                </div>
              </div>
              <div class="tier-item">
                <span class="tier-dot" style="--tier-ink: var(--tier-high)"></span>
                <div>
                  <strong>High risk</strong>
                  <p>Between 5% and 18%. One upset sinks it.</p>
                </div>
              </div>
              <div class="tier-item">
                <span class="tier-dot" style="--tier-ink: var(--tier-outrageous)"></span>
                <div>
                  <strong>Outrageous</strong>
                  <p>Under 5%. Every leg has to come in.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2>How tickets are picked</h2>
            <ul>
              <li>Each ticket picks legs from different events, so one bad result can't sink two legs.</li>
              <li>The four <TermLink id="tier">ways</TermLink> are the default. Other strategies screen every leg for value first, or let the maths decide how many legs to combine: a leg only belongs on a ticket if it is worth backing on its own.</li>
              <li>The same day always starts with the same tickets. Tap <strong>Deal more</strong> to add more from the same prices: different legs, nothing fetched. Nothing is ever taken off the list.</li>
              <li>A new ticket only uses events that haven't started yet, so you can still get it on. Turn that off in Settings if you're happy to ring a live leg through.</li>
              <li>Dealing more is free · nothing is fetched.</li>
            </ul>
          </section>

          <!-- The one thing about this screen that has to be explained, because
               it is the thing that changed. -->
          <section>
            <h2>Today is a timeline</h2>
            <ul>
              <li>Every ticket the app has proposed today is on one list, in the order the legs start. <strong>Now</strong> is the line between them: scroll up for what has been and gone, down for what is still to come. The screen opens on Now.</li>
              <li>It keeps going in both directions. Above today sit the days before it: every ticket the pad printed, with the result it got, one day at a time as you scroll back. Below it, the rest of today and then tomorrow once the books have posted their prices.</li>
              <li><strong>Earlier · Now · Later</strong> jumps: to the top of what has loaded, to the Now line, or to the next thing to start. It is not a filter (nothing is ever hidden), and it moves on its own as you scroll, to say where you are.</li>
              <li>A ticket <strong>expires</strong> when its first leg starts: a bookie won't take a multi with a leg under way. It stays on the list, printed quieter, with its legs reading "started" and any stamp it earned still on it. It is in History → Proposed either way, whether you backed it or not.</li>
              <li>When one expires, Legwork proposes another set off the same prices: never the same bets again, and never the same bets with a leg taken off or put on. Those say <strong>dealt 2:10 pm · prices from 9:11 am</strong>, so you know the numbers under them are still the morning's. It adds a few at a time, so the feed grows through the day rather than arriving all at once.</li>
              <li><strong>Nothing on this screen fetches anything</strong> except Refresh and pull-to-refresh, which are the same thing. Dealing, refilling and expiring all work off the prices already on your phone, and none of them costs a credit.</li>
            </ul>
          </section>

          <section>
            <h2>Odds and bookmakers</h2>
            <ul>
              <li>Prices come from your selected bookmaker. Switch bookmakers in Settings to compare.</li>
              <li>Prices are fetched once a day, after 7 am, through an odds account you connect, and shared with everyone. <strong>Refresh</strong> fetches them again, which costs about one credit per sport on the board, and buys today's racing card again where you have opened Racing. The app tells you the number before it spends anything. Your key stays on this device.</li>
              <li>Choose which boards you follow in Settings. Fewer boards, fewer credits.</li>
              <li>The free plan gives you 500 <TermLink id="credits">credits</TermLink> a month; a day of the Australian boards costs about 5–12.</li>
              <li>Once everything today has started, the pad rolls over to tomorrow.</li>
              <li><strong><TermLink id="marketbook">Which book is the market</TermLink></strong>, in Settings, is the price an edge is measured against: every book averaged, or one sharp book. It matters to strategies that screen for value; the Classic four don't measure edges.</li>
              <li>A multi goes on at one book, so every ticket says how far off the <TermLink id="bestprices">best prices across books</TermLink> it is: “9.0% off best price · benchmark $18.35”. That is a benchmark for how much the one slip gives up, not money you would have won: the events overlap, the prices move, and it takes an account at every book. The ticket screen names the book paying more, leg by leg.</li>
            </ul>
          </section>

          <!-- What the order of Today's sections means, and the ⓘ that names
               the two books it is worked out from. The words are PicksHelp's —
               the same component the old picks screen opened — so the two can
               never drift into describing one number two ways. -->
          <section>
            <h2>Ranked against the market</h2>
            <p>Every strategy is run over today's prices and its best ticket scored on one test: how much more your bookmaker pays than <TermLink id="market">the market</TermLink> says that ticket is worth. The list is ordered by start time, not by that score: the score rides on the ticket, as the badge beside its name.</p>
            <p>Best value is not most likely to win, and the chance is printed on every ticket. On a day when nothing beats the market, Today says so above the list.</p>
            <p><button type="button" class="link" @click="showWhoIsWho = true">Who is who?</button></p>
          </section>

          <!-- WHAT THE ⓘ ON BEST BETS OPENS. The bar is one sentence and it
               lives in `engine/proof.js`, built out of the numbers it is about,
               so a threshold that moves takes its own description with it.
               Printed, never paraphrased: a second copy of this in a template
               is a second bar to keep right. -->
          <section>
            <h2>Best bets, and the evidence bar</h2>
            <p>{{ PROOF_SENTENCE }}</p>
            <p>Most days nothing clears it, and that is the bar working rather than the app having nothing to say. The record strip under the heading is the half that moves while you wait: it counts every leg the pad has settled against the closing price, whether or not you backed it.</p>
            <p><TermLink id="proven">Proven</TermLink> · <TermLink id="clv">CLV</TermLink></p>
          </section>

          <section>
            <h2>Strategies and testing</h2>
            <ul>
              <li>Every strategy prints on Today, its name on the ticket it dealt. Hide any you don't follow from the Strategies tab.</li>
              <li><TermLink id="backtest">Backtest</TermLink> one over past seasons, at the prices that were actually on the board at the time.</li>
              <li>It reports the return, the strike rate, and whether it beat the <TermLink id="closing">closing line</TermLink>, and says so plainly when there are too few bets to tell you anything.</li>
              <li>Multis are a variance choice, not an edge amplifier. Longer odds are not better odds.</li>
              <li>Tap ⓘ on the Backtest screen for how a run works and how to read a result.</li>
            </ul>
          </section>

          <section>
            <h2>Placing bets</h2>
            <p>Tap <strong>Place bet</strong> to record a ticket here as Pending, then place it with your bookie. Legwork never takes a bet or holds money. Record the result in History once it has run, or let the morning settle do it.</p>
          </section>

          <p class="small-print glossary-line">
            Every term, in plain words: <TermLink>Glossary ›</TermLink>
          </p>
        </div>
      </IonContent>
    </IonModal>

    <!-- A sibling of the modal that opens it, not a child: Ionic stacks modals
         and dismisses the top one, and a modal nested inside another's content
         is torn down with its host. -->
    <Suspense>
      <PicksHelp :is-open="showWhoIsWho" @close="showWhoIsWho = false" />
    </Suspense>

    <!-- One modal for every section, handed whichever strategy was asked
         about — the same wiring the Strategies tab uses, for the same reason:
         eight sections would otherwise be eight copies of the same words. -->
    <Suspense>
      <StrategyExplainer
        :strategy="explaining"
        :is-open="!!explaining"
        @close="explaining = null"
      />
    </Suspense>

    <!-- Placing is the only thing on this screen that writes a record, and the
         stake is the one figure the pad guessed rather than knew — the pad
         prints every ticket at the Settings default and a punter does not
         always follow it. So it stops and asks, once, in the same idiom the
         prices bar uses before it spends a credit. -->
    <IonAlert
      :is-open="!!placing"
      header="Place bet"
      :sub-header="placeSubHeader"
      :message="placeMessage"
      :inputs="placeInputs"
      :buttons="placeButtons"
      @didDismiss="placing = null"
    />
  </IonPage>
</template>

<script setup>
import { computed, defineAsyncComponent, nextTick, ref as vRef, watch } from 'vue';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon, IonRefresher, IonRefresherContent,
  IonModal, IonHeader, IonToolbar, IonTitle, IonAlert,
  IonSegment, IonSegmentButton, IonLabel,
  IonInfiniteScroll, IonInfiniteScrollContent,
  onIonViewWillEnter, onIonViewDidEnter,
} from '@ionic/vue';
import {
  settingsOutline,
} from 'ionicons/icons';
import { useRouter } from 'vue-router';
import { AppHeader, useAction, toast } from '../core/ui';
import Ticket from '../components/Ticket.vue';
import KickoffGroup from '../components/KickoffGroup.vue';
import KindFilter from '../components/KindFilter.vue';
import BestBets from '../components/BestBets.vue';
import ProposedDay from '../components/ProposedDay.vue';
import TermLink from '../components/TermLink.vue';
import PricesBar from '../components/PricesBar.vue';
import AddLegSheet from '../components/AddLegSheet.vue';
import AskHome from '../components/ask/AskHome.vue';
import { flags } from '../lib/flags.js';
import { hasRacingCard, snapshot, placedIds } from '../store.js';
import { askView } from '../lib/stores/ask-view.js';
import {
  hasMarkets, oddsStatus, loadToday, autoLoadRacing, now, oddsCredits,
  timeline, foldIndex, dealMore,
  padResults, universe, riskProfile,
  padDate, todaySpent, pricesFetchedAt,
  nothingBeatsMarket, markPlaced, startedOnBoard, padStrategies,
  limits, onBreak, todayStaked, sampleMode, refusedToday,
  buildCandidates, previewBuild, buildMulti,
  hiddenToday, unhideTicket,
} from '../store.js';
import { breakLine, stakeAllowed } from '../lib/limits.js';
import { creditLine } from '../lib/credits.js';
import { getSports } from '../lib/odds.js';
import {
  feedOf, filterDays, filterFeed, hiddenLine, jumpTargets, regionOf,
} from '../lib/feed.js';
// Which kinds of ticket this screen is showing, and where that is remembered.
// The share card reads the same ref — you send what you are looking at.
import { feedFilter, setFeedKind, showEverything } from '../lib/stores/feed-filter.js';
// A strategy that deals from the racing card rather than from the board, read
// off its own document. The foot's "Nothing today" list is filtered with it.
import { isRacingStrategy } from '../lib/engine/strategies/racing.js';
// The words the foot control and its rows print. Pure, and shared with the
// ticket row that does the hiding — see lib/hide-copy.js.
import { RESTORED_LINE, hiddenFootLabel, hiddenRowLine } from '../lib/hide-copy.js';
// What a deal says it did. Pure, and the only place those sentences are built.
import { dealtLine, exhaustedLine } from '../lib/deal-copy.js';
import { localDate } from '../lib/tickets.js';
import { closingRowsFrom, livePricesFor } from '../lib/live-prices.js';
import { legsAsText } from '../lib/tickets.js';
import { lastOpened, openedLine } from '../lib/bookmakers.js';
import { copyText } from '../lib/clipboard.js';
import { kellyStakeLine, parseStake } from '../lib/stake.js';
import { TIER_LABEL, money, day } from '../lib/format.js';
import { CLASSIC } from '../lib/engine/strategies/classic.js';
import { state } from '../core/state.js';
// The refusal line is silent in the demo: the gate has not run over anybody's
// real morning there, and an audit of a gate that never ran is not an audit.
import { isDemo } from '../core/demo.js';

// Sign-in is the first gate, not the key. Someone who has not signed in (or
// is not on the allowlist yet) must not be told to paste an odds key they
// will only need once they are let in. The server checks the key header
// before the token — its tests pin that order — so the page puts the order
// right for the person reading it.
const gate = computed(() => (oddsStatus.value === 'unconfigured' && !state.user ? 'signed-out' : oddsStatus.value));
import { blankNoteFor, VERDICT_LINE } from '../lib/stores/pad-notes.js';
import { outcomes, proposedOn, trailToday } from '../lib/stores/paper-trail.js';
// The three with the most evidence, and the record they are printed under.
import { bestBets } from '../lib/best-bets.js';
import { keyFor, paperSummary, recordStrip } from '../lib/insights.js';
// The bar itself, in the one place it is written down. The ⓘ on the Best bets
// section opens the explainer this sentence is printed in.
import { PROOF_SENTENCE, proofStateOf } from '../lib/engine/proof.js';
import { expandByKey, paperByKey } from '../lib/insights-rollup.js';
import { rollup } from '../lib/stores/insights.js';
import { modelFits, provenFits } from '../lib/stores/models.js';

// `loadToday` refuses to fetch on a break itself — this is the same rule said
// where the reader of this file will look for it.
// AND THE RACING CARD WITH IT. Today prints racing tickets off a card this page
// never fetched, and until 2026-09-16 only an enter of the RACING tab could buy
// one — so the tickets on the feed could be quoting breakfast's prices at four
// in the afternoon. `autoLoadRacing` is the budget (an hour old, six pulls a
// day, `lib/racing/auto.js`); it costs nothing at all on the pulls it refuses,
// which is most enters of this page.
onIonViewWillEnter(() => { if (!onBreak.value) { loadToday(); autoLoadRacing(); } });

// Both help modals are fetched after the page is, the way Settings fetches
// ProvidersHelp: between them they carry every strategy explainer in the app,
// and Today is the screen a punter opens cold every morning to read the board.
// Nothing is waiting on them — they mount closed, so the chunk lands long
// before anyone taps a ⓘ.
const StrategyExplainer = defineAsyncComponent(() => import('../components/StrategyExplainer.vue'));
const PicksHelp = defineAsyncComponent(() => import('../components/PicksHelp.vue'));

const router = useRouter();
const showHowItWorks = vRef(false);
const showWhoIsWho = vRef(false);

// The dateline component, so the toolbar can pull its trigger. The confirm,
// the credit arithmetic and the words all stay in `PricesBar` — with the line
// they are about — and only the button that opens it moved up here.
const pricesBar = vRef(null);

// The STRATEGY, not its id: the section already holds the document the modal
// explains, so nothing here has to go looking it up a second way.
const explaining = vRef(null);
const explain = (strategy) => { explaining.value = strategy; };

// AND THE MODULE IS FETCHED AT MOUNT, NOT AT THE TAP. The whole card renderer
// is a chunk of its own so the app shell does not carry it, and a dynamic
// import inside the handler would be a network request between the tap and the
// share sheet. Safari hands out `navigator.share` on the tap's own activation
// and takes it back the moment something slow is awaited, so the promise is
// started here, while the page is being set up, and the tap only ever awaits
// one that has already settled.
//
// NEVER THE WHOLE LIST. With ASK on, the list's own Share menu sends the top
// three or five, or a hand-picked few; sharing every multi on the list was a
// picture nobody could read. The whole-pad share (`sharePad`) is unchanged for
// the pad without the flag, and for QA's hooks.
//
// A FEW MULTIS OFF THE LIST: one from its Details, the top three or five, or a
// hand-picked handful. Headed by what was asked, dated in the masthead, and
// each one's status said as the list says it. A second tap while it draws
// means "did that register", not "send it twice", hence the action's busy.
const shareMod = import('../lib/share-pad.js');
const shareSome = useAction(async (tickets) => (await shareMod).shareTickets(
  tickets.map((t) => {
    if (placedIds.value?.has(t.multi.id)) return { ...t, stamp: 'Placed' };
    // A leg that has started cannot be written any more, and a picture that
    // said nothing would read as a multi somebody could still go and back.
    if ((t.legs ?? []).some((l) => Date.parse(l.commenceTime) <= now.value)) return { ...t, stamp: 'Started' };
    return t;
  }),
  { title: askView.interpretation || 'Your multis', subtitle: day(padDate.value) },
));

async function shareChosen(tickets) {
  await shareSome.run(tickets);
  if (shareSome.error.value) toast(shareSome.error.value);
}

const open = (id) => router.push({ name: 'multi-detail', params: { id } });
const goSettings = () => router.push({ name: 'settings' });
// Straight to Pending, for the same reason: the tap came off a ticket that was
// just placed, and Pending is the pile it went into.
const goPending = () => router.push({ name: 'history', query: { tab: 'pending' } });

// By path, not by name: a wrong route name fails at the tap, and nothing in
// the build or the tests would have caught it.
const goSignIn = () => router.push('/sign-in');

// --- the sample board ----------------------------------------------------------
// The store puts it on the pad (`sampleMode`), refuses every write behind it and
// throws out of `markPlaced`. What is decided here is only how it READS: the
// card at the top, the banner that rides the scroll, and the actions taken off
// each ticket.

/**
 * Is the sample feed what this screen is showing? Both halves matter: a break
 * outranks it (the sheet is the only thing on the page), and a sample board
 * that failed to build has no feed to put a banner over — the full card below
 * prints instead.
 */
const sampleFeed = computed(() => sampleMode.value && hasMarkets.value && !onBreak.value);

/**
 * What this person is actually missing. `gate` already tells the two apart, and
 * the order is the server's: it checks the key header before the token, so
 * somebody who has neither is sent to sign in first and told about the key when
 * it is the thing in their way.
 */
const sampleCard = computed(() => (gate.value === 'signed-out'
  ? {
    title: 'This is a sample board',
    text: 'Real tickets, from a day that has been. Sign in and add your own key and this is today’s prices instead: the day is fetched once and shared with everyone who has access.',
    label: 'Sign in',
    note: 'No access yet? The sign-in page has a waitlist.',
    go: goSignIn,
  }
  : {
    title: 'This is a sample board',
    text: 'Real tickets, from a day that has been. Add a free key from The Odds API in Settings and the same pad runs on today’s prices.',
    label: 'Open Settings',
    note: 'Your key stays on this device. It’s sent securely over HTTPS to fetch odds, never stored on our server.',
    go: goSettings,
  }));

/**
 * What the boards this device has ticked will cost, in the one sentence
 * `lib/credits.js` owns. Read off `getSports()` rather than the board on the
 * pad: the sample board carries the demo's own fixtures, and quoting a figure
 * for those would be pricing somebody else's Saturday.
 */
const creditSentence = computed(() => creditLine(getSports()));

const blank = computed(() => {
  if (oddsStatus.value === 'loading') {
    return { title: 'Reading today’s prices', note: 'One moment.' };
  }
  // Today is FINISHED, not "not started yet", and tomorrow's board is not out.
  // "No markets open yet" would be wrong in the other direction — it reads as
  // the morning before the drop, when in fact the last match kicked off hours ago.
  if (todaySpent.value) {
    return {
      title: 'Everything today has started',
      note: 'Nothing left to print today. Tomorrow’s prices land when the books post them.',
    };
  }
  switch (oddsStatus.value) {
    case 'offline':
      return { title: 'No markets open yet', note: 'You’re offline. Tickets drop when today’s odds are in.' };
    case 'error': {
      // A generic upstream failure and a spent month of credits look the same
      // to this device — it only ever sees "the fetch failed" — but the last
      // credit count it was told about is a good enough signal to say which.
      const remaining = oddsCredits.value?.remaining;
      if (typeof remaining === 'number' && remaining < 15) {
        return { title: 'No markets open yet', note: 'Not enough credits for today’s odds.' };
      }
      return { title: 'No markets open yet', note: 'Couldn’t reach the odds. Pull down to try again.' };
    }
    default:
      return { title: 'No markets open yet', note: 'Tickets drop when today’s odds are in.' };
  }
});

// --- the timeline -------------------------------------------------------------
// The store owns the list, the ordering and the states (see `lib/timeline.js`).
// What is decided here is how it is READ: which half of it, where the fold
// prints, and what an old ticket's legs are worth now.

/**
 * Which of the three regions the top of the viewport is in. Written by the
 * SCROLL, read by the segment — see `updateRegion`. A tap on the segment does
 * not set it; it scrolls, and the scroll sets it.
 */
const region = vRef('now');

/** How many days of the paper trail are drawn above today. One = yesterday. */
const trailBack = vRef(1);

/** The whole trail keeps 60 days; nothing on the feed may reach past that. */
const TRAIL_MAX = 60;

// What a ticket dealt on an OLDER fetch of the prices is worth now: today's
// board at the same bookmaker, or the closing price for a leg whose match has
// been and gone, which the paper trail already holds for today. Nothing is
// fetched to work it out — this is the board the app already has.
const liveOptions = computed(() => ({
  universe: universe.value,
  bookmaker: riskProfile.value.bookmaker,
  closingRows: closingRowsFrom(outcomes.value[padDate.value]),
}));

/**
 * An entry with today's prices beside its own, but only where those are two
 * different readings. A ticket dealt on the board that is still on the pad IS
 * the board, so printing "now" beside its own prices would say nothing.
 */
function priced(entry) {
  if (!entry.pricesFrom || entry.pricesFrom === pricesFetchedAt.value) return entry;
  return {
    ...entry,
    ticket: { ...entry.ticket, live: livePricesFor(entry.ticket.legs, liveOptions.value) },
  };
}

/** The day the pad counts against, as a calendar day on the phone's clock. */
const todayKey = computed(() => localDate(now.value));

/**
 * Today's entries and tomorrow's, kept apart.
 *
 * Everything on `timeline` was dealt from a board the app already holds, and
 * once today is spent that board is TOMORROW's — so the same list carries both
 * days and the feed has to know which is which. A ticket with no kick-off to
 * read counts as today's: it is not a thing that happens tomorrow, it is a
 * thing with no time on it.
 */
const split = computed(() => {
  const all = timeline.value;
  const isToday = (e) => !e.cutoff || localDate(e.cutoff) === todayKey.value;
  // THE FILTER IS APPLIED HERE, BEFORE THE FEED IS BUILT, and everything
  // downstream follows from that: the clusters, the fold, the Now rule, the
  // three jumps and the share card are all arithmetic over this list. Filtering
  // the finished sections instead would leave a kick-off heading with nothing
  // under it and a rule counted against tickets nobody can see.
  const f = feedFilter.value;
  return {
    today: filterFeed(all.filter(isToday), f).map(priced),
    tomorrow: filterFeed(all.filter((e) => !isToday(e)), f).map(priced),
    // The fold counted in the SAME list it will be applied to — filtered by the
    // same filter, or the rule lands as many tickets adrift as the filter hid.
    // Tomorrow's tickets are all ahead of the clock, so they never move it.
    fold: filterFeed(all.slice(0, foldIndex.value).filter(isToday), f).length,
  };
});

/** Would the feed have anything on it at all with both kinds showing? */
const rawListed = computed(() => timeline.value.length > 0
  || trailDaysAll.value.some((d) => d.proposals.length > 0));

/** Every day of the trail the device actually holds, oldest first. */
const trailDates = computed(() => Object.keys(outcomes.value)
  .filter((d) => d < trailToday.value && Array.isArray(outcomes.value[d]) && outcomes.value[d].length)
  .sort()
  .slice(-TRAIL_MAX));

/** The days that are drawn, oldest first: the newest `trailBack` of them. */
const trailDaysAll = computed(() => trailDates.value
  .slice(-trailBack.value)
  .map((date) => ({ date, proposals: proposedOn(date) })));

/**
 * The same days with the filter over them, and a day left with nothing dropped.
 *
 * `trailBack` still counts RAW days, so "Earlier" pulls one more morning in per
 * reach whether or not that morning has anything the filter keeps — the reader
 * is walking back through the record, not through the filtered record.
 */
const trailDays = computed(() => filterDays(trailDaysAll.value, feedFilter.value));

/** Nothing older left to pull in. A phone with no trail at all says nothing. */
const trailDone = computed(() => trailBack.value >= trailDates.value.length);
const trailEnded = computed(() => trailDone.value && trailDates.value.length > 0);

/** The feed, in reading order — see `lib/feed.js`. */
const feed = computed(() => feedOf({
  timeline: split.value.today,
  foldIndex: split.value.fold,
  trailDays: trailDays.value,
  tomorrow: split.value.tomorrow,
  today: todayKey.value,
  now: now.value,
  trailDone: trailEnded.value,
}));

const sections = computed(() => feed.value.sections);
const targets = computed(() => jumpTargets(sections.value));

/** Is there a ticket on the feed at all, or only the rule and a note? */
const listed = computed(() => sections.value.some((s) => s.kind === 'group' || s.kind === 'day'));

const EMPTY_LINE = 'Nothing has been dealt today yet.';

/** The feed is empty because of the filter, not because the day is. */
const filteredOut = computed(() => !listed.value && rawListed.value && !!hiddenLine(feedFilter.value));

/**
 * The one line under an empty feed. Two different facts need two different
 * sentences: a day that printed nothing says so, and a day the reader has
 * filtered down to nothing says WHICH switch did it.
 */
const emptyLine = computed(() => (filteredOut.value ? hiddenLine(feedFilter.value) : EMPTY_LINE));

// The strategies that printed nothing at all — a quiet day is an answer, and a
// section that silently vanished would look broken. Gathered at the foot
// rather than interleaved: they are not things that start at a time.
const quiet = computed(() => padResults.value
  .filter((s) => !(s.tickets ?? []).some((t) => t.legs?.length))
  // AND ONLY THE KINDS THE READER IS LOOKING AT. A strategy deals from the
  // racing card or from the sports board — its own document says which
  // (`isRacingStrategy`) — and "Racing value: no runner cleared the edge test"
  // at the foot of a feed with racing switched off is an answer to a question
  // nobody asked, on the one screen where the reader has just said so.
  .filter((s) => (isRacingStrategy(s.strategy) ? feedFilter.value.racing : feedFilter.value.sports))
  .map((s) => ({
    id: s.strategy?.id ?? s.strategy?.name,
    name: s.strategy?.name ?? 'Strategy',
    note: blankNoteFor(s)
      || (s.tickets ?? []).find((t) => t.multi?.note)?.multi?.note
      || 'Nothing for this one on today’s prices.',
  })));

// At the foot of the feed, under everything that did print. What a strategy did
// not print today is not a thing that starts at a time, so it has no place on a
// clock — but it is still an answer, and an answer belongs on the screen.
const showQuiet = computed(() => quiet.value.length > 0);

/**
 * "14 legs refused today ›", or nothing at all.
 *
 * The count is the LOG's, not the gate's: `rejectionsFrom` caps a day at fifty,
 * and a line claiming three hundred against a record of fifty would be a number
 * nobody could check. Silent on the sample board and in the demo, where the
 * gate has not run over anybody's real morning.
 */
const refusedLine = computed(() => {
  if (sampleFeed.value || isDemo.value) return '';
  const n = refusedToday.value.length;
  if (!n) return '';
  return `${n} leg${n === 1 ? '' : 's'} refused today ›`;
});

/** Into Insights, where the refused legs are scored against the close. */
const goRefused = () => router.push({ name: 'insights' });

// The day's answer, once, above the list. It is about today's ranking as a
// whole, and the list is no longer ordered by it.
const showVerdict = computed(() => nothingBeatsMarket.value && hasMarkets.value);

// --- best bets ----------------------------------------------------------------
//
// The arithmetic is `lib/best-bets.js`; what is decided here is what it is
// handed and whether the sheet is on the screen at all.

/**
 * The ONLY fits a best bet may lean on: the proven ones. `provenFits` drops
 * every fit under t = 1.96, which is the same list `runStrategy` bets on — a
 * sheet reading the raw map would call a ticket "best" on a weight nobody
 * measured, on the one part of the screen that claims the most.
 */
const proven = computed(() => provenFits(modelFits.value));

/**
 * The paper trail per strategy key, in the long-named shape `insights.js`
 * reads: the same fold Insights runs on, over the same window, so the CLV line
 * under a best bet and the one on the Insights board cannot disagree about a
 * strategy on the same morning.
 */
const trailByKey = computed(() => expandByKey(paperByKey(
  { from: null, to: trailToday.value },
  { outcomes: outcomes.value, rollup: rollup.value },
)));

/**
 * What the whole trail has been worth against the close, as the strip prints
 * it — and, before there is enough of a trail to say, how much of one there is.
 *
 * `recordStrip` rather than `recordVsClose`: the strip is how a reader FINDS
 * this section, so it is on the screen every morning. The share card keeps the
 * other reading, where a claim about nothing is worse than no claim.
 */
const closeRecord = computed(() => recordStrip(paperSummary(trailByKey.value)));

/**
 * WHERE EACH STRATEGY ON THE PAD STANDS AGAINST THE BAR, one entry per
 * strategy: `engine/proof.js`'s four states, over the sports that strategy is
 * actually printing legs in today.
 *
 * A strategy printing in two sports is only as proven as its weakest one — a
 * fit is about a sport — so the state here is the lowest of them, and a ticket
 * whose eyebrow says "on trial" is admitting the half of the bar it has not
 * cleared rather than the half it has.
 */
const proofByStrategy = computed(() => {
  const order = ['control', 'unproven', 'on-trial', 'proven'];
  const out = {};
  for (const section of padResults.value) {
    const strategy = section.strategy;
    const id = strategy?.id;
    if (!id) continue;
    const sports = [...new Set((section.tickets ?? [])
      .flatMap((t) => (t.legs ?? []).map((l) => l.sport))
      .filter(Boolean))];
    if (!sports.length) continue;
    const agg = trailByKey.value[keyFor(id)] ?? null;
    const states = sports.map((sport) => proofStateOf(strategy, sport, { fits: proven.value, agg }));
    out[id] = states.reduce((lo, s) => (order.indexOf(s) < order.indexOf(lo) ? s : lo), 'proven');
  }
  return out;
});

/**
 * THE LIVE HALF OF TODAY, and only that. A ticket whose first leg has already
 * started is not something to bet, however good it was at eight this morning,
 * and yesterday's proposals on the roll above the rule are a record.
 */
const bestBetsModel = computed(() => bestBets({
  entries: split.value.today.filter((e) => e.ahead && !e.result),
  fits: proven.value,
  byKey: trailByKey.value,
  // The wait hints F5 will store. Nothing supplies them today, so every best
  // bet says "Bet now" — which is the truth, not a placeholder.
  features: null,
}));

/**
 * Is the sheet on the screen?
 *
 * NEVER OVER THE SAMPLE BOARD. The sample is a real day that has been, and a
 * recommendation off somebody else's Saturday is not a recommendation. A break
 * never reaches here — the break sheet owns the whole page above.
 *
 * It DOES print when nothing cleared: collapsed to one line that says so. A
 * sheet that vanished on a quiet morning would leave the reader wondering
 * whether the app had looked.
 */
const showBestBets = computed(() => !sampleFeed.value && hasMarkets.value);

// --- opening at now -----------------------------------------------------------
// The screen opens with the Now rule at the top of the scroll, so the next
// ticket to kick off is the first thing read and everything above it is a
// deliberate scroll upwards. Duration zero — there is no animation to reduce,
// which is `prefers-reduced-motion` honoured by not having the problem.
const content = vRef(null);

// The scroll element behind `ion-content`, resolved once the page is up.
// Ionic hands it out through a promise, and the scroll handler cannot wait on
// one per frame.
let scroller = null;

async function findScroller() {
  try {
    scroller = (await content.value?.$el?.getScrollElement?.()) ?? null;
  } catch {
    scroller = null;
  }
  return scroller;
}

/**
 * What is covering the top of the scroll: the toolbar the `fullscreen` content
 * runs under, plus the segment stuck beneath it. Anything scrolled "to the top"
 * has to clear both, or it lands behind them and the screen opens on nothing.
 */
function barInset() {
  const host = content.value?.$el;
  if (!host) return 0;
  const bar = (host.closest('.ion-page') ?? host.parentElement)?.querySelector('ion-header');
  const jumps = host.querySelector('.timeline-view');
  return (bar ? bar.getBoundingClientRect().height : 0)
    + (jumps ? jumps.getBoundingClientRect().height : 0);
}

/** The section with this key, as it stands in the page right now. */
function sectionEl(key) {
  const host = content.value?.$el;
  if (!host || !key) return null;
  // No escaping: every key `feedOf` mints is a prefix plus a date or an epoch
  // — `day-2026-09-13`, `f-1757824800000`, `now` — and a quoted attribute value
  // only has to fear a quote.
  return host.querySelector(`[data-feed-key="${key}"]`);
}

/**
 * One measure-and-scroll. Answers how far off it ended up, or null when there
 * was nothing to scroll to.
 */
async function scrollOnce(key) {
  await nextTick();
  const host = content.value?.$el;
  if (!host?.getScrollElement) return null;
  const target = sectionEl(key);
  if (!target) return null;
  try {
    const scroller = await host.getScrollElement();
    // LESS THE BAR. The content is `fullscreen`, so it scrolls UNDER the
    // toolbar — put the rule at scrollTop exactly and it lands behind it, and
    // the screen opens on the first kick-off with no rule in sight. The bar's
    // own height is the offset that makes "at the top" mean visible.
    const top = Math.max(0, Math.round(target.getBoundingClientRect().top
      - scroller.getBoundingClientRect().top
      + scroller.scrollTop
      - barInset()));
    host.scrollToPoint(0, top, 0);
    return Math.abs(scroller.scrollTop - top);
  } catch {
    return null; // the page went away mid-measure
  }
}

/**
 * Put a section at the top of the scroll, and CHECK THAT IT LANDED.
 *
 * One pass is not enough, and the reason is worth writing down: the page is
 * pinned as it is entered, and at that moment the scroller is still the height
 * of a page mid-transition. The browser clamps a scroll to the content it has,
 * so the first pass lands wherever the bottom of a half-built page happens to
 * be — measured at 250px against a rule sitting at 288 — and nothing afterwards
 * puts it right, because the scroll did not fail, it succeeded against the
 * wrong page. So it tries again across a few frames and stops the moment it
 * actually lands.
 */
async function scrollToSection(key, tries = 6) {
  for (let i = 0; i < tries; i += 1) {
    const off = await scrollOnce(key);
    if (off === null || off <= 2) return;
    await new Promise((done) => { requestAnimationFrame(() => done()); });
  }
}

const pinToNow = (tries = 6) => scrollToSection('now', tries);

// --- the three jumps ----------------------------------------------------------

/**
 * Where the top of the viewport is, as one of the three regions.
 *
 * Read off the DOM rather than off a scroll number, because the thing being
 * asked is "which section is under the bar" and only the sections know how tall
 * they are. Throttled: `ionScroll` fires on every frame and this walks the
 * page's section markers.
 */
function updateRegion() {
  const host = content.value?.$el;
  // MEASURED OFF THE SCROLLER, NOT OFF `ion-content`. The two are not the same
  // box: the content element's own rect already starts BELOW the toolbar, while
  // the scroller behind it starts at the top of the screen because the page is
  // `fullscreen`. Reading the host put the fold line fifty pixels low, which is
  // enough to call a section "passed" while it is still under the bar.
  if (!host || !scroller) return;
  const line = scroller.getBoundingClientRect().top + barInset() + 1;
  let index = -1;
  sections.value.forEach((s, i) => {
    const el = sectionEl(s.key);
    if (el && el.getBoundingClientRect().top <= line) index = i;
  });
  region.value = index === -1 ? 'earlier' : regionOf(sections.value, index);
}

let scrollTick = 0;
function onScroll() {
  const at = Date.now();
  if (at - scrollTick < 100) return;
  scrollTick = at;
  updateRegion();
}

// --- the two kinds -------------------------------------------------------------
// Sports and racing, as two independent switches. Both on is the whole feed and
// is what a phone that has never been told otherwise opens on; the choice is
// remembered in `pref.todayFeedFilter` (lib/stores/feed-filter.js) and the
// share card reads the same ref, so a picture of Today is a picture of the
// Today in front of the person sending it.
//
// The control itself is `components/KindFilter.vue`, shared with History. It
// owns the two glyphs and the struck-out mark; this page owns what a tap MEANS
// here, which is the scroll below.

/**
 * Turn one kind on or off, and KEEP THE READER WHERE THEY WERE.
 *
 * Tickets appearing or vanishing above the viewport move the page under the
 * thumb — the same problem `loadEarlier` solves by measuring — and there is no
 * arithmetic that can fix it here, because what changed is the list itself. So
 * the screen re-pins to the one landmark that survives every filter: the Now
 * rule, which is where Today opens and the only place on the feed a reader can
 * always find their way back from.
 */
async function toggleKind(kind) {
  setFeedKind(kind, !feedFilter.value[kind]);
  await nextTick();
  await scrollToSection('now', 3);
  updateRegion();
}

/** The way out of a feed the reader has filtered down to nothing. */
async function showAll() {
  showEverything();
  await nextTick();
  await scrollToSection('now', 3);
  updateRegion();
}

/**
 * The segment was tapped. It is a jump, so the value it was set to is discarded
 * the moment the scroll lands and `updateRegion` writes the real one back.
 *
 * Earlier also PULLS ONE MORE DAY IN. The top of the feed is not a fixed place
 * — it is however far back the reader has asked to go — so a jump upward that
 * only ever landed on the same morning would stop being a direction after the
 * first tap.
 */
async function onJump(event) {
  const want = event?.detail?.value;
  if (!want) return;
  region.value = want;
  if (want === 'earlier' && !trailDone.value) {
    trailBack.value += 1;
    await nextTick();
  }
  await scrollToSection(targets.value[want] ?? 'now', 3);
  updateRegion();
}

/**
 * One more day above what is drawn, WITHOUT the page moving under the reader.
 *
 * Content inserted above the viewport pushes everything down by its own height,
 * which on a scroller means the reader is suddenly looking at something else.
 * The fix is the same one every chat log uses: measure the scroll height before
 * and after, and add the difference back to `scrollTop`. That also keeps the
 * Now rule exactly where it was, which is the whole promise of open-at-now.
 */
async function loadEarlier(event) {
  const el = scroller ?? (await findScroller());
  const before = el?.scrollHeight ?? 0;

  const grew = !trailDone.value;
  if (grew) trailBack.value += 1;

  await nextTick();
  await new Promise((done) => { requestAnimationFrame(() => done()); });

  if (el && grew) {
    const delta = el.scrollHeight - before;
    if (delta > 0) el.scrollTop += delta;
  }
  updateRegion();
  event?.target?.complete?.();
}

// When the page was entered, so a pin can tell "still settling" from "the
// reader is looking at it".
let enteredAt = 0;

onIonViewDidEnter(() => {
  enteredAt = Date.now();
  // Back to yesterday and no further, every time the screen is opened: the
  // feed is read from Now outwards, and a reader who scrolled back a fortnight
  // this morning is not asking for a fortnight of paper this afternoon.
  trailBack.value = 1;
  findScroller().then(() => pinToNow()).then(updateRegion);
});

// AND AGAIN WHILE THE PAGE IS STILL SETTLING. The rule is measured against a
// layout that is not finished at `didEnter`: the yesterday line arrives with
// the paper trail and the verdict with the ranking, and each of them pushes the
// rule down after it has been scrolled to. A pin that only ran once landed
// forty pixels short on a phone and fifty-six at 320, where the same sentence
// wraps. Bounded to a second and a half so it can never yank a page somebody
// has started reading.
//
// AND IT MUST NOT FIGHT THE LAZY DAYS. `trailBack` changes the sections too, so
// the watcher would re-pin the page every time a day loaded in at the top —
// undoing the scroll correction `loadEarlier` just made. The 1.5-second window
// is what keeps the two apart: settling happens as the page opens, loading
// happens when somebody has scrolled to the top of it.
watch([sections, showVerdict], () => {
  if (Date.now() - enteredAt < 1500) pinToNow();
});

// Only Classic deals a different four from the same board — the seed is its
// tie-break. A model strategy is deterministic, so on a model-only pad the
// button would print the same set again; pull-to-refresh is what moves those.
const canDeal = computed(() => padStrategies.value.some((s) => s.id === CLASSIC.id));

// DEAL MORE ADDS; it never takes anything away. It re-runs the strategies over
// the board already on the device — free, no fetch — and puts whatever is new
// on the timeline, up to the per-tier cap.
//
// The seed is a tie-break, and on a thin board every seed breaks the same way:
// `dealMore` walks forward until the deal changes and answers false when none
// of them do. Saying so is the whole point — an unchanged list and a button
// that did nothing look identical.
//
// AND IT SAYS WHAT IT DID. The tickets land in KICK-OFF order — scattered up a
// feed whose foot is where this button is — so a deal that answered only
// "nothing more" was indistinguishable from a deal that worked. `dealMore` now
// answers what landed and where, and `lib/deal-copy.js` is the only place those
// names become a sentence.
//
// Three dead ends, and they need different answers. A board whose spare matches
// have all kicked off is one, and it has a setting behind it — so it is said
// first, and says which, rather than blaming the size of a board that was big
// enough an hour ago. A board that offered tickets the feed is already holding
// is another, and `exhaustedLine` names the tiers it happened in. The third is
// a board too thin to print anything at all, which is the old eight words.
function dealMoreTickets() {
  const dealt = dealMore();
  if (dealt.added) {
    toast(dealtLine(dealt));
    return;
  }
  if (!riskProfile.value.allowStarted && startedOnBoard.value > 0) {
    toast('Everything still to start is already on the feed. Events under way are skipped; allow them in Settings.');
    return;
  }
  toast(exhaustedLine(dealt) || 'Nothing more to deal from today’s prices');
}

// --- what was hidden ------------------------------------------------------------
// The other end of the Hide on a ticket's own row. The store holds the list
// (`hiddenToday`, keyed by the ticket's legs and stored against the day); this
// is the control that offers them back, one at a time.

/** Collapsed until asked for: a control for a rare change of mind, not a feed. */
const showHidden = vRef(false);

/**
 * Put one back, and say where it went. The feed is in kick-off order, so a
 * restored ticket lands wherever its first leg starts — which is rarely beside
 * the control that put it back, and a punter watching this spot for it to
 * reappear would watch a long time.
 */
function restore(id) {
  if (!unhideTicket(id)) return;
  if (!hiddenToday.value.length) showHidden.value = false;
  toast(RESTORED_LINE);
}

// --- build a multi --------------------------------------------------------------
// The pad is what the app thinks. This is where the punter says what they
// think, off the same board at the same prices — the picker from the ticket
// screen, opened on nothing. It costs no credits and it prints a proposal the
// paper trail scores like any other.
const building = vRef(false);

/** Nothing to build from, a borrowed board, or a break: no builder. */
const canBuild = computed(() => hasMarkets.value && !sampleMode.value && !onBreak.value);

// Read when the sheet opens rather than held as a computed over the clock: the
// list is a reading of the board taken at the moment somebody asked for it, and
// a ranking that re-sorted under a reader's thumb every minute would be the
// screen arguing with the hand on it.
const buildRows = vRef([]);

function openBuilder() {
  buildRows.value = buildCandidates();
  building.value = true;
}

/**
 * Print it, and go to it. The ticket screen is where a ticket is read and
 * placed, so a builder that left somebody on the feed hunting for what they
 * just made would have stopped one step short.
 *
 * THROWS rather than catching, so the sheet prints the refusal beside its own
 * button — the same contract the Add-a-leg sheet has.
 */
function printBuilt(picks) {
  const done = buildMulti(picks);
  if (!done) return;
  building.value = false;
  // Not a refusal: the ticket they were building already exists, so they are
  // taken to it rather than told off for asking twice. And no "Printed" toast
  // on the way through — the ticket itself is the next thing on the screen,
  // and a toast confirming what somebody is already looking at is noise.
  if (done.duplicate) toast('That exact ticket is already on the feed');
  // The punter's own book had no price on one of the legs. A statement, not a
  // refusal: it went on at the best price on the board.
  else if (done.note) toast(done.note);
  open(done.id);
}

// Why a strategy printed nothing now lives in `stores/pad-notes.js`: the share
// card prints every section of Today, blank ones included, so the picture and
// the screen have to reach for the same sentence.

/**
 * PULL TO REFRESH, which is the same call the toolbar's button makes.
 *
 * Through `PricesBar` rather than straight to the store, so one gesture cannot
 * quietly become two again: the credit confirm, the busy state and the words
 * all live in that component, and both triggers come through it.
 */
async function refresh(event) {
  try {
    await pricesBar.value?.refresh();
  } finally {
    event.target.complete();
  }
}

// --- placing from the board ---------------------------------------------------
// The ticket a punter has asked to place, held while the alert is up. It IS the
// alert's open state — there is no second flag to keep in step with it.
const placing = vRef(null);

// Before the drop the sports board is empty on purpose (`ODDS_DROP_HOUR`), and
// the ask screen still has the racing card — or at least a sentence about when
// the prices open — which is better than the old "no markets yet".
const beforeDropAsk = computed(() => !!snapshot.value?.dropsAt && !pricesFetchedAt.value);
const askToPlace = (ticket) => {
  limitNote.value = '';
  // Read once, as the alert opens, rather than watched: `lastOpened` is a
  // breadcrumb dropped by the row's own "Open at Neds" (lib/bookmakers.js) and
  // this is the only moment it is worth anything.
  openedNote.value = openedLine(lastOpened(ticket?.multi?.id));
  placing.value = ticket;
};

/**
 * The limit's refusal, printed in the alert itself.
 *
 * It replaces the alert's own message rather than arriving as a toast: the
 * figure that was refused is still in the box, and the sentence has to sit
 * beside it to be read as being about that figure. Cleared when the alert opens
 * again, so yesterday's refusal is not the first thing on today's.
 */
const limitNote = vRef('');

/**
 * "Opened Neds · tap Place bet when it's on." — where the row before this one
 * sent the slip to a book.
 *
 * It REPLACES the usual line rather than joining it. "Legwork keeps the record
 * — place it with your bookie" is the same instruction said in general terms,
 * and a punter who has just come back from Neds is owed the specific one.
 */
const openedNote = vRef('');

const PLACE_MESSAGE = 'Legwork keeps the record · place it with your bookie.';

/**
 * WHERE THE PREFILLED FIGURE CAME FROM (engine review #13): "¼ Kelly of $500 ·
 * capped at 2%", in front of the usual line.
 *
 * The amount in the box used to be the flat default out of Settings, which
 * needs no explaining. A Kelly stake is a number nobody typed, worked out from
 * the edge and the bankroll, and a punter about to put it on is owed the rule
 * it came from — beside the figure, where it can be read as being about it.
 * Empty on a flat ticket, and on a pad with no bankroll set.
 */
const kellyLine = computed(() => kellyStakeLine(placing.value?.multi, riskProfile.value.bankroll));

const placeMessage = computed(() => limitNote.value
  || openedNote.value
  || (kellyLine.value ? `${kellyLine.value} · ${PLACE_MESSAGE}` : PLACE_MESSAGE));

const placeSubHeader = computed(() => {
  const m = placing.value?.multi;
  if (!m) return '';
  const tier = TIER_LABEL[m.tier] ?? 'Ticket';
  // The same naming a ticket's own label uses: Classic's tiers are the whole
  // name, and anything else is its strategy with the tier after it.
  const name = m.label && m.strategyId !== CLASSIC.id ? `${m.label} · ${tier}` : tier;
  return `${name} · ${money(m.combinedOdds)}`;
});

// Prefilled with the ticket's own stake — the Settings default — because that
// is what the ticket was printed at. What actually went on is whatever the
// punter types over it, recorded once here rather than corrected in History.
const placeInputs = computed(() => {
  const stake = Number(placing.value?.multi?.stake);
  return [{
    name: 'stake',
    type: 'number',
    value: Number.isFinite(stake) ? stake.toFixed(2) : '',
    min: 0.5,
    step: 0.5,
    // "Stake", not "Stake placed": nothing has been placed yet — this is the
    // figure being asked for, and the past tense belongs in History, where the
    // bet actually is one.
    attributes: { inputmode: 'decimal', 'aria-label': 'Stake' },
  }];
});

// `placing.value` is read the moment the work runs, which is inside the
// handler's own tick — before the alert has dismissed and cleared it.
const place = useAction(
  (amount) => markPlaced(placing.value, amount),
  { done: 'Placed · pending in History', weight: 'pop' },
);

// The alert is gone by the time this finishes, so a failure has nowhere on the
// screen to land except a toast — where the confirmation lands too.
async function placeBet(amount) {
  await place.run(amount);
  if (place.error.value) toast(place.error.value);
}

// Refusing by returning false keeps the alert — and the figure already typed
// into it — on screen. Dismissing on a bad number would make the punter open
// it again and start the stake from scratch to fix one character.
function confirmPlace(data) {
  const amount = parseStake(data?.stake);
  if (amount === null) {
    toast('Enter what you are putting on, like 10.');
    return false;
  }
  // The limit the punter set themselves, checked before the record is written
  // rather than after. `markPlaced` refuses too — it is the choke point — but a
  // refusal that arrived as a toast over a closed alert would leave them
  // reopening it and typing the stake again to find out what the limit was.
  const allowed = stakeAllowed({
    stake: amount,
    todayStaked: todayStaked.value,
    ...limits.value,
  });
  if (!allowed.ok) {
    limitNote.value = allowed.text;
    return false;
  }
  placeBet(amount);
  return undefined;
}

const placeButtons = computed(() => [
  { text: 'Cancel', role: 'cancel' },
  { text: 'Place bet', handler: confirmPlace },
]);

// --- copying legs -------------------------------------------------------------
const copy = useAction((ticket) => copyText(legsAsText(ticket?.legs ?? [])), { done: 'Copied legs' });

// The button is inside a section, with no line under it to print an error on,
// so the one failure this can have is said where the success is said.
async function copyLegs(ticket) {
  await copy.run(ticket);
  if (copy.error.value) toast(copy.error.value);
}
</script>

<style scoped>
/* --- the top of the page --------------------------------------------------- */
/* One line: the dateline. What yesterday did is no longer a sentence up here —
   it is the first thing above the Now rule, as tickets. */

/* --- the timeline ---------------------------------------------------------- */
/* The frame, the pill and the type all come from the shared `ion-segment`
   block in app.css — the same control History's three piles and the Matches
   chips use. What is local is that it STAYS.

   Three jumps that scroll off the top of the page are three jumps nobody can
   take: the screen opens pinned to Now, which on a day with a paper trail above
   it is two thousand pixels down, and the control was last seen at the top of
   yesterday. Sticky at `top: 0` inside a `fullscreen` ion-content lands it
   directly under the toolbar — the scroll element carries the bar's height as
   padding, and a sticky box is held against the padding edge, not the viewport.
   The pad's own colour behind it, because the tickets pass underneath. */
.timeline-view {
  position: sticky;
  top: 0;
  /* ABOVE EVERY SHEET, not two above the desk. The tickets scroll underneath
     this bar and it is the one thing on the page that may never be scrolled
     over: 2 was enough against `position: static` paper, and a ticket's own
     `position: relative` rows (the ⓘ sits at 1 inside one) are the kind of
     thing that ends up in its own compositing layer on a phone, where paint
     order is decided per layer rather than per rule. 4 leaves the sample
     banner above it, which is the one thing that outranks it. */
  z-index: 4;
  margin: var(--sp-3) 0 0;
  padding: var(--sp-1) 0;
  /* Opaque, and the pad's own colour: `fullscreen` content runs under the
     toolbar, so anything transparent here prints tickets through the bar. */
  background-color: var(--pad);
}

/* ONE BAR, TWO CONTROLS, AND NO SECOND ROW. The jumps take whatever is left
   after the filter, which is a fixed 92px: the filter is two tap targets and
   cannot shrink, and the jumps have three words that can. `min-width: 0` is
   what lets the segment do that instead of pushing the filter off the end. */
.jump-bar {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}

.jump-bar .jumps {
  flex: 1 1 auto;
  min-width: 0;
}

/* AT 320 THE WORDS SHRINK, THEY DO NOT WRAP. Three jumps and two toggles want
   about 300px at the full size; the filter cannot give any of it back (a tap
   target is a tap target), so the labels come down a step and lose their
   tracking. "Earlier" at 12px is still a word; "Earli…" is not, and two rows
   of control above the first ticket is worse than either. */
@media (max-width: 360px) {
  .jump-bar .jumps {
    --segment-label-size: var(--fs-small);
  }

  .jump-bar :deep(ion-segment-button) {
    font-size: var(--fs-small);
    letter-spacing: 0;
    --padding-start: 2px;
    --padding-end: 2px;
  }
}

/* The way back out of an empty feed, directly under the line that says what is
   hidden. `.deal-more` is the foot-of-the-list idiom this borrows — a text
   action at the tap floor, in a sentence's own size. */
.show-all {
  margin-top: var(--sp-2);
}

/* THE FOLD, on the desk between two sheets rather than on either of them. The
   ruled variant of the app's own section heading: a line across the page, the
   word under it, and nothing else — it is a boundary, not a section. Its top
   margin is deliberately larger than the gap between two sheets so the rule
   reads as separating the day rather than as labelling the sheet below it. */
.now-rule {
  margin-top: var(--sp-6);
}

/* Nothing on the feed at all. One line of small print on the desk, where the
   sheets would have been. */
.timeline-empty {
  margin: var(--sp-5) 0 0;
}

/* The two ends of the roll — "That's the whole record" at the top, "Tomorrow's
   board lands when the prices drop" at the foot. Both are the app saying where
   the paper runs out, so both are on the desk rather than on a sheet, and
   neither is a button: one would be a fetch nobody asked for. */
.feed-note {
  margin: var(--sp-4) 0;
  text-align: center;
}

/* --- the break -------------------------------------------------------------- */
/* One sheet where the whole day's paper was. It is deliberately the only thing
   on the screen above the terms: a quieter version of the feed would still be
   the feed, and the point of a break is that there is nothing to read. */
.break-sheet {
  margin: var(--sp-5) 0 var(--sp-6);
}

.break-title {
  margin: 0;
  font: 700 var(--fs-head)/1.25 var(--font-body);
  color: var(--carbon);
}

.break-text {
  margin: var(--sp-2) 0 0;
  font: 500 var(--fs-body)/1.45 var(--font-body);
  color: var(--carbon);
}

/* --- the quiet strategies --------------------------------------------------- */
/* One sheet at the foot for every strategy that printed nothing. A line each:
   a blank ticket per strategy was a ticket's worth of paper spent saying there
   is no ticket, and four of them on an evening board was most of a screen. */
.quiet-sheet {
  margin: var(--sp-6) 0;
}

.quiet-title {
  margin-top: 0;
}

.quiet-note {
  margin: 0 0 var(--sp-2);
}

.quiet-sheet > :last-child {
  margin-bottom: 0;
}

/* The strategy's own name, carbon against the faint sentence explaining it, so
   a reader scanning the foot of the page finds the names first. */
.quiet-note strong {
  color: var(--carbon);
}

/* --- the day's verdict ----------------------------------------------------- */
/* Carbon on the pad's own paper, not a banner: it is a sentence the app is
   saying about the sections underneath it, and boxing it would make it look
   like a warning about the app rather than about the board. */
.verdict {
  margin: var(--sp-4) 0 0;
  font: 700 var(--fs-small)/1.45 var(--font-body);
  color: var(--carbon);
}

/* The bar's title used to be pulled to the start HERE, for this screen only,
   because Today is the one carrying five actions. Every tab root lost its
   large title afterwards, which made the bar the only place any screen prints
   its name — so the rule belongs to all of them and now lives in app.css. */

/* --- onboarding ------------------------------------------------------------ */
.onboarding {
  padding: var(--sp-4) 0 var(--sp-6);
}

.onboarding-title {
  margin: 0 0 var(--sp-3);
  font: 700 var(--fs-head)/1.2 var(--font-body);
  color: var(--carbon);
}

.onboarding-text {
  margin: 0 0 var(--sp-4);
  font: 500 var(--fs-body)/1.5 var(--font-body);
  color: var(--carbon);
}

.onboarding-steps {
  margin: 0 0 var(--sp-5);
  padding: 0 0 0 var(--sp-5);
  font: 500 var(--fs-body)/1.8 var(--font-body);
  color: var(--carbon);
}

.onboarding-steps a {
  color: var(--carbon);
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.onboarding-note {
  margin: var(--sp-4) 0 0;
  font: 500 var(--fs-small)/1.45 var(--font-body);
  color: var(--carbon-faint);
}

/* --- the sample board ------------------------------------------------------- */
/* The card above a feed rather than instead of one, so it gives up the bottom
   half of its padding: the banner is next, and the board is directly under
   that. */
.onboarding--sample {
  padding-bottom: var(--sp-4);
}

/* The credit maths reads as the figure it is, not as another paragraph. */
.sample-credits {
  font-weight: 700;
}

/* ONE LINE, AND IT RIDES THE SCROLL. Not over the tickets and not a dialog in
   front of them — a rule across the top of the page that stays there, so a
   reader who has scrolled into the middle of the board can still find out
   whose board it is.

   Stuck at 0 with a ground of its own: `ion-content` is `fullscreen`, so the
   page runs under a translucent toolbar and a transparent banner would print
   this sentence over the tickets sliding beneath it. It sits one above the
   jump bar, and see `.pad-page--sample .timeline-view` below for why the two
   never fight for the same top edge. */
.sample-banner {
  position: sticky;
  top: 0;
  z-index: 5;
  margin: 0;
  padding: var(--sp-2) 0;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background-color: var(--pad);
  font: 700 var(--fs-small)/1.4 var(--font-body);
  color: var(--carbon);
}

/* TWO STICKY BARS AT THE SAME TOP EDGE IS ONE BAR AND A BUG. The banner wins,
   because it is the sentence that keeps the screen honest; the jump control
   goes back into the flow for as long as the sample is up. Nothing is lost by
   it: the sample is a single day with no paper trail above it, so Earlier · Now
   · Later has almost nothing to jump between, and two stacked bars would take a
   third of a 320px screen to say it. */
/* THE PAGE IS A SCREEN TALL BEFORE ANYTHING IS IN IT. The content under the
   toolbar mounts a beat after the shell (the board, then the answer), and the
   Gambling Help footer below it was drawn near the top of the screen first and
   then pushed down: the app shell's layout shift on Lighthouse, all of it that
   footer. Reserving the screen keeps the footer below the fold from the start. */
.pad-page:not(.how-it-works) {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
}
.pad-page:not(.how-it-works) > .terms {
  margin-top: auto;
}
/* On the builder, a little more than a screen: at exactly one screen the
   footer's first line still sat on the bottom edge and was nudged when the
   tickets landed. */
.pad-page--ask {
  min-height: calc(100vh + 8rem);
  min-height: calc(100dvh + 8rem);
}

.pad-page--sample .timeline-view {
  position: static;
}

/* EVERY TICKET'S ACTION, REPLACED BY THE ONE THING TO DO.
   Place bet on a sample ticket is a button that can only fail — `markPlaced`
   throws under `sampleMode`, which is the choke point — so the control goes and
   the line says what to do instead, exactly as `TicketActions` does it on a
   break.

   IT IS DONE FROM HERE, IN CSS, AND THAT IS NOT WHERE IT BELONGS. TicketActions
   has had a `disabled`/`note` seam since the break landed, and the right fix is
   to thread those two props through KickoffGroup from the entry — which is a
   change to a component this slice does not own. The rule is scoped to
   `.ticket-actions`, so it cannot reach anything else on the page, and the card
   above carries the real 44px control. Replace this with the props the moment
   KickoffGroup is free. */
.pad-page--sample :deep(.ticket-actions) > * {
  display: none;
}

.pad-page--sample :deep(.ticket-actions)::after {
  content: 'Add a key to place this';
  display: block;
  font: 500 var(--fs-small)/1.45 var(--font-body);
  color: var(--carbon-faint);
}

/* --- how it works modal ---------------------------------------------------- */
.how-it-works section {
  padding-bottom: var(--sp-4);
  border-bottom: 1px solid var(--rule);
  margin-bottom: var(--sp-4);
}

.how-it-works section:last-child {
  border-bottom: 0;
}

.how-it-works h2 {
  margin: 0 0 var(--sp-3);
  font: 700 var(--fs-body)/1.25 var(--font-body);
  color: var(--carbon);
}

.how-it-works p {
  margin: 0 0 var(--sp-2);
  font: 500 var(--fs-body)/1.5 var(--font-body);
  color: var(--carbon);
}

.how-it-works ul {
  margin: 0;
  padding: 0 0 0 var(--sp-5);
}

.how-it-works li {
  margin: 0 0 var(--sp-2);
  font: 500 var(--fs-body)/1.5 var(--font-body);
  color: var(--carbon);
}

/* The last line of the modal: the way out to the whole glossary. Named inside
   `.how-it-works` because the paragraph rule above it would otherwise win on
   specificity and print it at body size. */
.how-it-works .glossary-line {
  margin: 0;
  font: 500 var(--fs-small)/1.45 var(--font-body);
  color: var(--carbon-faint);
}

.tier-explain {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.tier-item {
  display: flex;
  gap: var(--sp-3);
  align-items: flex-start;
}

.tier-dot {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--tier-ink);
}

.tier-item strong {
  display: block;
  font: 700 var(--fs-body)/1.25 var(--font-body);
  color: var(--carbon);
}

.tier-item p {
  margin: 2px 0 0;
  font-size: var(--fs-small);
}
.deal-more {
  margin: var(--sp-4) 0 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
}

/* The hidden list, at the foot. Quiet by construction — it is a way back, not
   a second feed — so it is small print on the desk rather than a sheet, and
   every control in it is a text action at the tap floor. */
.hidden-foot {
  margin: var(--sp-4) 0 0;
}

.hidden-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.hidden-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  min-height: 44px;
  border-bottom: 1px solid var(--rule);
}

.hidden-row:last-child {
  border-bottom: 0;
}

/* `min-width: 0` so a long strategy name shrinks itself rather than pushing
   Show off the end of the row at 320. */
.hidden-name {
  min-width: 0;
  margin: 0;
}

/* `.link` takes the SIZE OF ITS SENTENCE, which is right for "See them ›"
   inside a line of prose and wrong here: this one stands alone at the end of a
   row, so it is given the tap floor in both directions rather than the 39px
   the word happens to measure. */
.hidden-show {
  flex: 0 0 auto;
  min-width: 44px;
  text-align: right;
}
</style>
